'use client';

import { useMemo, type CSSProperties } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { findWikiPath, useWikiSource, type WikiTreeNode } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { currentWikiSlug } from './wikiSlug';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Docs breadcrumb trail for a wiki page — {@code Home / Section / Page}.
 * Self-fetches the tree and walks {@link findWikiPath} for the current
 * route (from {@link usePathname}); the leaf (current page) renders as
 * plain text, ancestors as links. Drops into the top of the
 * {@code wiki-detail} layout so every doc page reads like a real
 * knowledge base.
 *
 * <p>Renders nothing when there's no bound page (landing/search) or the
 * tree hasn't loaded — the surrounding chrome stays clean rather than
 * showing an empty rail.
 */
export interface WikiBreadcrumbsProps {
  /** Root crumb label. Default "Wiki". */
  homeLabel?: string;
  /** Root crumb href. Default "/wiki". */
  homeHref?: string;
  /** Show the root crumb. Default true. */
  showHome?: boolean;
}

export function WikiBreadcrumbs({
  homeLabel: authoredHomeLabel,
  homeHref = '/wiki',
  showHome = true,
}: WikiBreadcrumbsProps) {
  const { t } = useTranslation('site-builder-blocks');
  const homeLabel = authoredHomeLabel ?? t('wiki.homeLabel');
  const wiki = useWikiSource();
  const pathname = usePathname() ?? '';
  const slug = useMemo(() => currentWikiSlug(pathname), [pathname]);

  const treeState = useAsyncResource<WikiTreeNode[]>(async () => {
    if (!wiki) return null;
    const tree = await wiki.getTree();
    return tree.length > 0 ? tree : null;
  }, [wiki]);

  const chain = useMemo(() => {
    if (treeState.kind !== 'loaded' || !slug) return [];
    return findWikiPath(treeState.data, slug);
  }, [treeState, slug]);

  if (!slug || chain.length === 0) return null;

  return (
    <BlockRoot as="nav" aria-label={t('shared.nav.breadcrumb')} style={navStyle}>
      <ol style={listStyle}>
        {showHome && (
          <li style={itemStyle}>
            <Link href={homeHref} style={linkStyle}>
              {homeLabel}
            </Link>
            <Separator />
          </li>
        )}
        {chain.map((node, i) => {
          const isLast = i === chain.length - 1;
          return (
            <li key={node.slug} style={itemStyle}>
              {isLast ? (
                <span aria-current="page" style={currentStyle}>
                  {node.title}
                </span>
              ) : (
                <>
                  <Link href={`/wiki/${node.slug}`} style={linkStyle}>
                    {node.title}
                  </Link>
                  <Separator />
                </>
              )}
            </li>
          );
        })}
      </ol>
    </BlockRoot>
  );
}

function Separator() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" style={separatorStyle} aria-hidden="true">
      <path d="M5 3 L9 7 L5 11" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

const navStyle: CSSProperties = {
  margin: '0 0 var(--space-3, 12px)',
  fontSize: 'var(--font-size-sm, 13px)',
};

const listStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 2,
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const itemStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
};

const linkStyle: CSSProperties = {
  color: 'var(--color-text-secondary, #71717a)',
  textDecoration: 'none',
};

const currentStyle: CSSProperties = {
  color: 'var(--color-text-primary, #18181b)',
  fontWeight: 600,
};

const separatorStyle: CSSProperties = {
  margin: '0 2px',
  color: 'var(--color-text-tertiary, #a1a1aa)',
  flexShrink: 0,
};
